import { useState, useEffect, useRef } from "react";
import { walletApi, contributionsApi } from "@/lib/api";
import { toast } from "sonner";

export default function WalletTopupDialog({ open, onClose, onCredited }) {
  const [limits, setLimits] = useState({ minTopup: 200, maxTopup: 50000 });
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  useEffect(() => {
    if (!open) { stopPolling(); setBusy(false); return; }
    walletApi.me().then(({ data }) => setLimits({ minTopup: data.minTopup, maxTopup: data.maxTopup })).catch(() => {});
    return stopPolling;
  }, [open]);

  if (!open) return null;

  const handleTopup = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (amt < limits.minTopup || amt > limits.maxTopup) {
      toast.error(`Amount must be between KES ${limits.minTopup} and KES ${limits.maxTopup}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await walletApi.topup(amt);
      toast.success("STK Push sent — confirm on your phone");
      const cid = data.checkoutRequestId;
      let elapsed = 0;
      const intervalMs = 4000;
      stopPolling();
      pollRef.current = setInterval(async () => {
        elapsed += intervalMs;
        try {
          const { data: s } = await contributionsApi.stkStatus(cid);
          if (s.status === "success") {
            stopPolling();
            toast.success(`Wallet credited! Receipt ${s.mpesaReceiptNumber || "—"}`);
            setAmount(""); setBusy(false);
            onCredited?.();
            onClose();
          } else if (s.status === "failed") {
            stopPolling();
            toast.error(s.resultDesc || "M-Pesa cancelled the request");
            setBusy(false);
          }
        } catch { /* keep polling */ }
        if (elapsed >= 90000 && pollRef.current) {
          stopPolling();
          toast.warning("Still waiting for M-Pesa confirmation. Refresh to check.");
          setBusy(false);
        }
      }, intervalMs);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Top-up failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="topup-dialog">
      <div className="bg-card rounded-xl border border-border w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">Top up wallet</h3>
          <button onClick={onClose} disabled={busy} className="text-muted-foreground" data-testid="topup-close-btn">×</button>
        </div>
        <p className="text-sm text-muted-foreground">An M-Pesa STK push will be sent to your registered phone. The wallet is credited as soon as M-Pesa confirms.</p>
        <div>
          <label className="text-xs font-medium block mb-1">Amount (KES)</label>
          <input
            type="number"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`Min ${limits.minTopup} · Max ${limits.maxTopup}`}
            data-testid="topup-amount-input"
            className="w-full px-3 py-2 text-base bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
            disabled={busy}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[200, 500, 1000, 2000].filter(v => v >= limits.minTopup && v <= limits.maxTopup).map(v => (
            <button key={v} onClick={() => setAmount(String(v))} disabled={busy} data-testid={`topup-preset-${v}`} className="px-3 py-1.5 rounded-lg bg-secondary text-xs font-medium">KES {v}</button>
          ))}
        </div>
        <button
          onClick={handleTopup}
          disabled={busy || !amount}
          data-testid="topup-confirm-btn"
          className="w-full py-2.5 rounded-xl bg-emerald-600 text-white font-semibold text-sm active:scale-95 disabled:opacity-60"
        >
          {busy ? "Waiting for M-Pesa…" : "Send STK Push"}
        </button>
      </div>
    </div>
  );
}
